import { canCallOperation } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { commandGate, gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { handoffPointLabel } from '../monitoring/robotTransferModel.ts'

/**
 * 모듈 「초기화」 버튼의 게이트 — **서버 가드와 같은 차례로** 사유 하나를 고른다.
 *
 * 서버(`ctcells-core/app/views/module_control.py` `_plain(mc.INITIALIZE)` → `_relay` · v0.9.1 `0d1a977`)의 차례:
 *
 * | 차례 | 서버 가드 | 거절 | 화면 사유 |
 * | --- | --- | --- | --- |
 * | 0 | — | — | 구독 stale → `reason.streamStale`(§6 규약 · 서버와 무관하게 막는다) |
 * | 1 | `require_roles(WRITE_ROLES)` = operator·admin | 403 `INSUFFICIENT_ROLE` | 권한 없음 |
 * | 2 | `require_control_lock` | 409 `CONTROL_LOCK_HELD` | 제어락 필요 |
 * | 3 | `control_owner == 'local'` | 409 `MODULE_LOCAL_MODE` | 단독 모드 |
 * | 4 | (초기화만) 로봇 파지 중(`module_prepare.holds_tray`) | 409 `MODULE_BUSY` `reason=robot_holding_tray` | **막지 않는다** — 서버 문구가 버튼 옆에 선다 |
 * | 5 | 시스템 안전 상태 · 모듈 자신(`ERROR` 면 `MODULE_IN_ERROR`) | 409 `E_STOP` · 모듈 거절 | **막지 않는다** — 서버 응답이 버튼 옆에 선다 |
 *
 * 🔴 **「도는 작업이 있으면 막는다」 축을 걷었다**(v0.9.1 · core `75fca33` 「배치 중에도 제어 화면을 쓰고, 파지 중에는
 *    초기화만 막는다」 · 사용자 2026-09-27 「사람이 제어는 배치중이든 아니든 할 수 있어야해」). 서버가 그 가드를 지웠는데
 *    화면이 흉내 내면 누를 수 있는 버튼을 막는다 — 되살리지 마라.
 * ⚠ 파지 판정(차례 4)은 화면이 재구성하지 않는다 — 서버 `holds_tray` 는 상태 · `grips` · 모듈 종류를 함께 보는 판정이라
 *   옮겨 적으면 두 번째 정의처가 된다. 거절 문구는 서버 카탈로그 `message` 그대로 온다(`errorMessage`).
 *
 * 🔴 **권한은 `/auth/me` 의 `operations` 가 이긴다** — `initializeModuleControl` 이 있으면 된다.
 *    `operations` 를 모르면(`null`) 권한 5종의 `control`(= operator·admin)로 떨어진다(`WRITE_ROLES` 와 같은 둘).
 * 🔴 **`allowed_commands` 에 `initialize` 가 없어도 막지 않는다**(팀장 지시 2026-09-28). 이 버튼이 필요한
 *    바로 그 순간 — 취소 뒤 모듈이 `PIPETTING` 에 남았을 때 — 집합이 **빈다**(실측: IDLE 넷도 전부 `[]`).
 *    그때 막으면 푸는 길이 사라진다. 대신 `advisory` 로 「서버 판정에 맡긴다」를 적는다.
 * ⚠ 차례 3 의 재료는 서버 값이다(`ModuleSnapshot.control_owner`). 화면이 상태를 지어내지 않는다 — 서버가
 *   반드시 거절할 요청을 누르게 두지 않을 뿐이다.
 */
export const INITIALIZE_OPERATION = 'initializeModuleControl' as const
export const INITIALIZE_COMMAND = 'initialize' as const

export interface InitializeGateInput {
  access: ScreenAccess
  /** `GET /auth/me` 의 `operations`. 모르면 `null` */
  operations: readonly string[] | null
  /** `ModuleSnapshot.control_owner` */
  controlOwner: string
  /** `ModuleSnapshot.allowed_commands` **그대로** */
  allowedCommands: readonly string[]
  /** 요청을 보내는 중 */
  sending: boolean
}

export interface InitializeGate {
  gate: ActionGate
  /** 활성이지만 서버 집합이 이 명령을 말하지 않았을 때의 안내. 비활성이면 없다 */
  advisory?: string
}

export function initializeGate(input: InitializeGateInput): InitializeGate {
  const { access, operations } = input

  /* 권한 축만 `operations` 로 갈아 끼운다 — stale · 락은 공용 `gate()` 가 같은 차례로 본다 */
  const permitted =
    operations === null
      ? (access.permissions?.includes('control') ?? false)
      : canCallOperation(operations, INITIALIZE_OPERATION)
  if (access.controlDisabled === true)
    return { gate: { disabled: true, disabledReason: t('reason.streamStale') } }
  if (!permitted)
    return {
      gate: { disabled: true, disabledReason: t('reason.noPermission', { perm: 'control' }) },
    }
  const base = gate({ ...access, permissions: ['control'] }, { perm: 'control', needsLock: true })
  if (base.disabled) return { gate: base }

  if (input.controlOwner === 'local')
    return { gate: { disabled: true, disabledReason: t('reason.moduleLocalMode') } }
  if (input.sending)
    return { gate: { disabled: true, disabledReason: t('reason.requestInFlight') } }

  /*
   * 🔴 `allowed_commands` 의 `includes` 는 `commandGate` 한 곳에서만 한다(`access.ts` 규약). 앞의 축은
   *    이미 다 통과했으니 여기서 비활성이면 그 사유는 «집합에 없음» 하나뿐이다 — 그것을 막지 않고 안내로 돌린다.
   */
  const listed = commandGate(
    { ...access, permissions: ['control'] },
    input.allowedCommands,
    INITIALIZE_COMMAND,
    'control',
  )
  return listed.disabled
    ? { gate: { disabled: false }, advisory: t('moduleInit.advisory') }
    : { gate: { disabled: false } }
}

/**
 * `handoff_occupancy` 실물 모양(2026-09-28 `GET /modules`):
 * `[{ point_id, occupied, basis, updated_at }]`. 계약은 자유 객체(`anyOf [{}, null]`)라 칸별로 읽는다.
 * 🔴 `occupied === true` 만 센다 — 모르는 것을 점유로 적지 않는다.
 */
export function occupiedPointsOf(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return []
    const { occupied, point_id: pointId } = entry as { occupied?: unknown; point_id?: unknown }
    return occupied === true && typeof pointId === 'string' && pointId !== '' ? [pointId] : []
  })
}

/**
 * 점유 자리 원값(`assembler.grip_tray_on_holder`)의 사람이 읽는 이름(「거치대 위」). 이름표 사전은 모니터링 6축 라인과 한 벌이다
 * (`handoffPointLabel` · `monitoring.robot.point.*`). 🔴 사전에 없으면 **원값 그대로** — 이름을 지어내지 않는다.
 */
export function occupiedPointLabel(pointId: string): string {
  const dot = pointId.indexOf('.')
  const tail = dot < 0 ? pointId : pointId.slice(dot + 1)
  const label = handoffPointLabel(tail)
  return label === tail ? pointId : label
}

/* ═══════════════ 「제어」 카드의 다른 쓰기(파지 기억 · 복구 잠김 · 진단) ═══════════════ */

/** 파지 기억 초기화 `POST /modules/{id}/grips/forget`(`IFC-RO-53`) */
export const FORGET_GRIPS_OPERATIONS = ['forgetModuleGrips'] as const
/**
 * 자동 복구 잠김 풀기 `POST /modules/{id}/recovery/reset`(`IFC-RO-31`).
 * ⚠ 서버 operationId 가 `resetModuleRecoveryLatch` → `resetModuleRecoveryLock` 으로 바뀌었다(로컬 `/auth/me` 실측 2026-10-22) — 둘 다 받는다
 */
export const RESET_RECOVERY_OPERATIONS = [
  'resetModuleRecoveryLock',
  'resetModuleRecoveryLatch',
] as const
/** 진단 중계 `POST /modules/{id}/control/diagnostics`(`IFC-RO-48`) */
export const DIAGNOSTICS_OPERATIONS = ['runModuleDiagnosticsControl'] as const

export interface ModuleWriteGateInput {
  access: ScreenAccess
  /** `GET /auth/me` 의 `operations`. 모르면 `null` */
  operations: readonly string[] | null
  /** 이 조작의 operationId(하나라도 있으면 된다) */
  operationIds: readonly string[]
  /** `ModuleSnapshot.control_owner` */
  controlOwner: string
  sending: boolean
}

/**
 * 「제어」 카드의 다른 쓰기 단추 게이트 — 「초기화」(`initializeGate`)와 **같은 차례**다.
 *
 * | 차례 | 서버 가드(셋 다 같다 · `require_roles(WRITE_ROLES)` + `require_control_lock`) | 화면 사유 |
 * | --- | --- | --- |
 * | 0 | — | 구독 stale |
 * | 1 | operator · admin(403) | 권한 없음 |
 * | 2 | 제어락(409 `CONTROL_LOCK_HELD`) | 제어락 필요 |
 * | 3 | 진단만 `control_owner == 'local'`(409 `MODULE_LOCAL_MODE`) — 화면은 셋 다 막는다(단독 모드면 모듈 로컬 화면이 쥔다) | 단독 모드 |
 *
 * 🔴 `allowed_commands` 축이 없다 — 이 셋을 가리키는 어휘가 그 집합에 없다(계약에 어휘 미선언 · 실측 assembler-1 `["confirm_load","confirm_unload"]`).
 *    지어내 막지 않는다. 모듈의 거절(409)은 서버 문구 그대로 그 줄 아래에 선다.
 */
export function moduleWriteGate(input: ModuleWriteGateInput): ActionGate {
  const { access, operations } = input
  const permitted =
    operations === null
      ? (access.permissions?.includes('control') ?? false)
      : input.operationIds.some((id) => operations.includes(id))
  if (access.controlDisabled === true)
    return { disabled: true, disabledReason: t('reason.streamStale') }
  if (!permitted)
    return { disabled: true, disabledReason: t('reason.noPermission', { perm: 'control' }) }
  const base = gate({ ...access, permissions: ['control'] }, { perm: 'control', needsLock: true })
  if (base.disabled) return base
  if (input.controlOwner === 'local')
    return { disabled: true, disabledReason: t('reason.moduleLocalMode') }
  if (input.sending) return { disabled: true, disabledReason: t('reason.requestInFlight') }
  return { disabled: false }
}
